// The dashboard project selector (architecture sprint-15 §9.3). A native select (no Radix Select is installed)
// with the selected project's colour swatch. One candidate → a static label; a running timer or open shift →
// disabled with a hint; no candidates → not rendered.
import { ColorSwatch } from '@/components/ColorSwatch';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import type { SelectedProjectState } from '@/hooks/use-selected-project';

export const LOCKED_HINT = 'Stop your timer or end your shift to change project.';
export const NO_PROJECT_LABEL = 'No project';
const SELECT_ID = 'dashboard-project';

export function DashboardProjectSelector({ state }: { state: SelectedProjectState }): JSX.Element | null {
  const { selection, candidates, select } = state;
  if (candidates.length === 0 && !selection?.locked) return null;
  const project = selection?.project ?? null;
  if (candidates.length === 1 && !selection?.locked) {
    const only = candidates[0];
    return (
      <p className="mb-6 flex items-center gap-2 text-sm text-foreground" aria-label="Project">
        {only && <ColorSwatch hex={only.color} />}
        {only?.name}
      </p>
    );
  }
  const locked = selection?.locked === true;
  const value = project?.id ?? '';
  // The running project may be one the list doesn't hold (e.g. archived mid-run): keep it as an option.
  const extra = locked && project && !candidates.some((c) => c.id === project.id) ? [project] : [];
  return (
    <div className="mb-6 w-full max-w-xs space-y-1">
      <Label htmlFor={SELECT_ID}>Project</Label>
      <div className="flex items-center gap-2">
        {project && <ColorSwatch hex={project.color} />}
        <NativeSelect id={SELECT_ID} value={value} disabled={locked} onChange={(e) => e.target.value && select(e.target.value)}>
          {value === '' && <option value="">{locked ? NO_PROJECT_LABEL : 'Choose a project'}</option>}
          {[...candidates, ...extra].map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </NativeSelect>
      </div>
      {locked && <p className="text-xs text-muted-foreground">{LOCKED_HINT}</p>}
    </div>
  );
}
